/*
    Single-selector with all users in a company
*/

import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { GetAllCompanyUsersDocument, UserNameNoCompanyFragment } from '@willow/graphql-iso/src/app';
import { CompanyId, UserId } from '@willow/types-iso';

import { getUserName } from '../../../utils';
import { Loader } from '../../Loader/Loader';
import { Options, ReactSelectOverrides, WillowSelect } from './WillowSelect';

type Props<T extends FieldValues> = {
  companyId: CompanyId;
  fieldName: FieldPathByValue<T, UserId | undefined>;
  control: Control<T>;
  userOverrides?: UserNameNoCompanyFragment[]; // either subset of users passed in or all users in company
  hideErrorMessage?: boolean;
  label?: ReactNode;
  placeholder?: string;
  isClearable?: boolean;
  required?: boolean;
  reactSelectOverrides?: ReactSelectOverrides;
  disabled?: boolean;
};

export function ControlledCompanyUserSingleSelect<T extends FieldValues>({
  companyId,
  fieldName,
  control,
  userOverrides,
  hideErrorMessage = false,
  label,
  placeholder = 'Select',
  isClearable = false,
  required,
  reactSelectOverrides,
  disabled = false,
}: Props<T>) {
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const { data, loading } = useQuery(GetAllCompanyUsersDocument, {
    variables: {
      companyId,
    },
    skip: !!userOverrides,
  });
  if (!userOverrides && (loading || !data)) return <Loader />;

  const allUsers = userOverrides ?? data?.company.allUsers ?? [];
  const OPTIONS: Options = allUsers.map((u) => {
    return { value: u.id, label: getUserName(u) };
  });

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}

      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WillowSelect
            placeholder={placeholder}
            options={OPTIONS}
            value={value ? OPTIONS.filter((r) => value === r.value) : null}
            isClearable={isClearable}
            onChange={(e) => onChange(e?.value ?? '')}
            isDisabled={disabled}
            {...reactSelectOverrides}
          />
        )}
      />
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
